import { PLATFORM_TIMEZONE } from '../config/timezones';
import { isKnownZone } from './zonedClock';

/**
 * What a collection's Time zone setting shows (docs/plans/analytics.md §4.4,
 * the owner's ruling q06), from the collection as the settings form holds it:
 *
 *   collection.timezone          the form's own value — a zone set here, or
 *                                null / absent to inherit
 *   collection.timezoneResolved  keepr-api's answer at load:
 *                                { zone, source: 'collection' | 'parent' |
 *                                'platform', from?, fallback?, ignored? }
 *
 * The server's answer supplies what the collection INHERITS: `fallback` when
 * it had its own zone at load, otherwise the answer itself. Whether a zone is
 * SET HERE is the form's value — so an unsaved pick or reset shows at once
 * and a save needs no second read — except when that value is exactly the
 * stored one the server said it skipped (`ignored`: a name the runtime no
 * longer knows, or one keepr refuses). That value is not in use, so it is not
 * presented as "This collection".
 *
 * Returns null when the response carries no `timezoneResolved` (an API that
 * predates the setting, or a collection not saved yet): the field is not
 * shown rather than written against a server that would not validate it.
 *
 *   own           true when the collection sets its own zone, in use
 *   ownZone       that zone (the picker's value), else null
 *   inheritedZone what it uses without one (the picker's placeholder)
 *   mark          the SourceMark's words: "This collection" (a chip) when
 *                 set here; "Inherited from {collection}" or "keepr's
 *                 default" when inherited
 *   fallbackText  where "Reset to inherited" lands, named with its zone
 *   ignored       a stored value the server skipped, while the form still
 *                 holds it (the reset clears it), else null
 */
export function describeCollectionTimezone( collection ) {
	const resolved = collection?.timezoneResolved;
	if ( ! resolved || typeof resolved.zone !== 'string' || ! resolved.zone ) return null;

	const inherited = resolved.source === 'collection'
		? ( resolved.fallback && typeof resolved.fallback.zone === 'string' ? resolved.fallback : { zone: PLATFORM_TIMEZONE, source: 'platform' } )
		: resolved;
	const fromParent = inherited.source === 'parent';
	const parentName = typeof inherited.from?.name === 'string' && inherited.from.name.trim() ? inherited.from.name.trim() : '';

	const formValue = typeof collection.timezone === 'string' && collection.timezone ? collection.timezone : null;
	const skipped = resolved.source !== 'collection' && typeof resolved.ignored === 'string' && formValue === resolved.ignored;
	const ownZone = formValue && ! skipped ? formValue : null;

	const inheritedWords = fromParent ? `Inherited from ${ parentName || 'the parent collection' }` : 'keepr\'s default';

	return {
		own: Boolean( ownZone ),
		ownZone,
		inheritedZone: inherited.zone,
		mark: ownZone
			? { word: 'This collection', phrase: 'Set on this collection', own: true }
			: { word: inheritedWords, phrase: inheritedWords, own: false },
		fallbackText: fromParent
			? `Falls back to ${ parentName ? `${ parentName }'s` : 'the parent collection\'s' } time zone, ${ inherited.zone }.`
			: `Falls back to keepr's default, ${ inherited.zone }.`,
		ignored: skipped ? formValue : null,
	};
}

/**
 * The zone a collection's days are counted in, for the defaults a new item
 * starts with (the owner's ruling B11, 2026-10-01: "today" is the
 * COLLECTION's, the way the server reads it): keepr-api's resolved answer
 * (`timezoneResolved.zone` — the collection's own zone, else the nearest
 * collection above it that sets one), else keepr's default. Also takes the
 * public form's descriptor zone as a bare string. A name this browser's Intl
 * does not know falls back too, rather than throwing in the form.
 */
export function collectionZone( collectionOrZone ) {
	const zone = typeof collectionOrZone === 'string' ? collectionOrZone : collectionOrZone?.timezoneResolved?.zone;
	return isKnownZone( zone ) ? zone : PLATFORM_TIMEZONE;
}

/**
 * Is the collection's zone KNOWN yet? False while the collection is loading,
 * so a new item's form can wait before it seeds a today / now / relative
 * default — seeding in keepr's default and never correcting it is the bug the
 * ruling exists to remove. A failed load counts as known (the fallback stands).
 */
export function collectionZoneKnown( context ) {
	if ( ! context ) return true;
	return Boolean( context.collection ) || Boolean( context.collectionError ) || ( ! context.collectionLoading && ! context.collectionId );
}
